import { Component, ChangeDetectionStrategy, signal, inject, effect, computed } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { toSignal } from '@angular/core/rxjs-interop';
import { PageTabsComponent, PageTab } from 'src/app/core/components/page-tabs/page-tabs.component';
import { ItdrService, DataSyncStatus } from './services/identity-security.service';
import { ItdrDashboardComponent } from './components/dashboard/itdr-dashboard.component';
import { ItdrPostureComponent } from './components/posture/itdr-posture.component';
import { ItdrMissingIntegrationComponent } from './components/empty-states/itdr-missing-integration.component';
import { ItdrFirstSyncComponent } from './components/empty-states/itdr-first-sync.component';

type CustomerState = 'missing-integration' | 'first-sync' | 'ready' | 'loading';

@Component({
  selector: 'app-itdr',
  standalone: true,
  imports: [
    CommonModule,
    PageTabsComponent,
    ItdrDashboardComponent,
    ItdrPostureComponent,
    ItdrMissingIntegrationComponent,
    ItdrFirstSyncComponent,
  ],
  templateUrl: './identity-security.page.html',
  styleUrl: './identity-security.page.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class IdentitySecurityComponent {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private itdrService = inject(ItdrService);

  /** Page-level data freshness (last identity-provider pull), shown for both tabs. */
  public dataSync = signal<DataSyncStatus | null>(null);

  public tabs: PageTab[] = [
    { id: 'dashboard', label: 'Identity Threat Detection & Response' },
    { id: 'posture', label: 'Identity Security Posture Management' },
  ];

  /**
   * URL is the single source of truth for both active tab and deep-link target.
   * Tab clicks update the URL; the effect below mirrors URL changes into state signals.
   * Without this, repeat clicks of "Manage this risk" in the score dialog produce a URL
   * change that the @switch never sees.
   */
  private queryParams = toSignal(this.route.queryParamMap, { requireSync: true });

  public activeTab = signal<string>('dashboard');
  public targetCheckId = signal<string | null>(null);

  /**
   * True until the effect below has processed the URL for the first time on this
   * component instance. A fresh entry to the page (sidebar click, browser back,
   * a bookmark) must always land on the dashboard tab: the layout preserves every
   * query param when navigating between pages, so a stale `?tab=posture` from an
   * earlier ISPM visit would otherwise reopen ISPM on return. In-page deep-links
   * ("Manage this risk") happen while the component is already alive, so firstLoad
   * is already false and their `tab=posture` is honoured.
   */
  private firstLoad = true;

  /**
   * Active organization, the same `?organizationId=` the rest of the customer pages read.
   * Passed down to the widgets already wired to the real API, which identify their
   * organization in the path.
   */
  public organizationId = computed<number | null>(() => {
    const raw = this.queryParams().get('organizationId');
    const parsed = raw != null ? Number(raw) : NaN;
    return Number.isFinite(parsed) ? parsed : null;
  });

  /** Drives the outer @switch: integration missing, first sync still running, or fully ready. */
  public customerState = computed<CustomerState>(() => {
    const s = this.dataSync();
    if (!s) return 'loading';
    if (s.missing_integrations.length > 0) return 'missing-integration';
    if (!s.initial_sync_complete) return 'first-sync';
    return 'ready';
  });

  constructor() {
    effect(
      () => {
        const params = this.queryParams();
        const tab = params.get('tab') ?? 'dashboard';
        const check = params.get('check');

        // A fresh entry to the page ALWAYS lands on the dashboard tab, dropping any
        // `tab`/`check` the layout carried over from another page (it preserves every
        // query param across page navigations, so a prior ISPM visit would otherwise
        // reopen ISPM on return). A real posture deep-link ("Manage this risk") is an
        // in-page action fired while this component is already alive -- firstLoad is
        // false by then -- so this reset only ever catches a leaked param, never an
        // intentional one.
        if (this.firstLoad && (tab !== 'dashboard' || check)) {
          this.firstLoad = false;
          this.router.navigate([], {
            relativeTo: this.route,
            queryParams: { tab: 'dashboard', check: null },
            queryParamsHandling: 'merge',
            replaceUrl: true,
          });
          return;
        }
        this.firstLoad = false;

        this.activeTab.set(tab);
        this.targetCheckId.set(check);
      },
      { allowSignalWrites: true }
    );

    // Refetch whenever the active organization changes.
    //
    // On failure the state is left as loading rather than null-and-forgotten: the
    // page has no branch for "we could not tell", and defaulting to any of the three
    // real states would either hide a working dashboard or claim a setup problem
    // that may not exist.
    effect(
      () => {
        const organizationId = this.organizationId();
        this.dataSync.set(null);
        if (organizationId == null) return;

        this.itdrService.getDataSyncStatus(organizationId).subscribe({
          next: (res) => this.dataSync.set(res),
          error: (err) => console.error('Failed to load data sync status:', err),
        });
      },
      { allowSignalWrites: true }
    );
  }

  public onTabChange(tabId: string): void {
    // Push to URL; clear any stale deep-link check so it doesn't re-expand on tab switch.
    this.router.navigate([], {
      relativeTo: this.route,
      queryParams: { tab: tabId, check: null },
      queryParamsHandling: 'merge',
    });
  }
}
